import { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import {
  approvePendingTherapistChanges,
  getPendingTherapists, rejectPendingTherapistChanges
} from '../../../api/services/therapistService';
import Layout from '../../../components/layout';
import { Notification } from '../../../components/notifications';
import LayoutError from '../../../components/layoutError';
import DataTable from '../../../components/dataTable';
import EditModal from '../../../components/editModal';
import { useHistory } from 'react-router-dom';
import User from '../../../api/models/user';
import moment from 'moment';
import { getStates } from '../../../api/services/stateService';
import State from '../../../api/models/state';
import { Role } from '../../../common/constants';
import { useAppSelector } from '../../../app/hooks';
import { approvePendingBonus, rejectPendingBonus } from '../../../api/services/bonusServices';
import Bonus from '../../../api/models/bonus';
import payrate from '../../../api/models/payrate';
import { getDistrictOptions } from '../../../api/services/districtService';
import { selectOption } from '../../../types/selectOption';
import { hasRole } from '../../../common/utils';

/**
 * ApproveTherapist Component
 */
const ApproveTherapist = () => {
  const history = useHistory();
  const [therapistsData, setTherapistsData] = useState<User[]>([]);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [openRejectConfirmModal, setOpenRejectConfirmModal] = useState<boolean>(false);
  const [openRejectConfirmBonusModal, setOpenRejectConfirmBonusModal] = useState<boolean>(false);
  const [therapistId, setTherapistId] = useState<number>(0);
  const [bonusId, setBonusId] = useState<number | undefined>(0);
  const [states, setStates] = useState<State[]>([]);
  const userRoles = useAppSelector((s) => s.user.roles);
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [feedback, setFeedback] = useState<string>("");
  const [districts, setDistricts] = useState<selectOption[]>([]);

  /**
   * Gets the states
   */
  const statesData = useAsync({
    promiseFn: getStates,
    onResolve: (r) => setStates(r.data.states)
  });

  /**
   * Gets the districts
   */
  const districtsData = useAsync({
    promiseFn: getDistrictOptions,
    onResolve: (r) => setDistricts(r.data)
  });

  /**
   * Primary API call to get the therapists with pending changes
   */
  const { isPending } = useAsync({
    promiseFn: getPendingTherapists,
    onResolve: (r) => setTherapistsData(r.data)
  });

  /**
   * Formats properties
   */
  const pendingChangePropertyLabel = (property: string) => {
    property = property.replace("Id", "");
    property = property.replace(/([A-Z])/g, ' $1').trim(); //add space between upper case
    property = property.charAt(0).toUpperCase() + property.slice(1); //Captilize first letter

    return property;
  }

  /**
   * Populates the pending change
   */
  const pendingChangeValueLabel = (property: string, value: string | any[]) => {

    if (property === "stateId")
      return states.find((s) => s.stateId === Number(value))?.name

    if (property === "payrates")
      return value.length + " pending payrates";

    if (property === "payratesToDelete")
      return value.length + " delete request";

    if (value === "" || value === undefined)
      return <div className='italic'>empty</div>;

    return value;
  }

  /**
   * Populates the pending payrates
   */
  const populatePendingPayrates = (prop: any) => {
    const currencyUsDollar = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    return <>
      <tr></tr>
      {((prop[0] === "payrates" || prop[0] === "payratesToDelete") && prop[1].length > 0) && (
        <>
          <tr key={`pc-prop-key-payrates-pending`} className="pt-0 pb-0 mt-0 mb-0 text-left justify-left">
            <td></td>
            <td className="font-bold"><span>Pending Payrates</span></td>
          </tr>
          {prop[1].map((payrate: payrate) => (
            <tr key={`pc-prop-key-payrates-pending-${payrate.payrateId}`} className="pt-0 pb-0 mt-0 mb-0 text-left justify-left">
              <td></td>
              <td colSpan={2} className="relative font-semibold right-10">
                <span className='block italic'>{`${districts.find(x => x.value === payrate.districtId)?.label}`}</span>
                <span className="block italic">{`${moment(payrate.startDate).format('MM/DD/YYYY')}-${moment(payrate.endDate).format('MM/DD/YYYY')}, ${currencyUsDollar.format(Number(payrate.rate ?? 0))}`}</span></td>
            </tr>
          ))}
        </>
      )}
    </>
  }

  /**
   * DataTable Columns
   */
  const columns = () => [
    {
      Header: 'Therapist Name',
      accessor: (s: any) => s.lastName + ', ' + s.firstName
    },
    {
      Header: 'Changes',
      accessor: (s: any) => {
        const pendingChanges: any[] = Object.entries(JSON.parse(s.pendingChanges ?? '{}'));
        const userProperties: any[] = Object.entries(s);
        let disciplineIdProp: any = pendingChanges.find(x => x[0].toLowerCase() == 'disciplineid');

        return (
          <table id="therapistApproveTable" className="table-fixed" key={`thch-${s.userId}`}>
            <thead>
              <tr className="pt-0 pb-0 mt-0 mb-0 text-left justify-left">
                <th>Old</th>
                <th></th>
                <th>New</th>
              </tr>
            </thead>
            <tbody>
              <Fragment>
                {pendingChanges.map((prop: any[], key: number) => {
                  let p = userProperties.length > 0 ? userProperties.find((u) => {
                    if (u[0].toLowerCase() == prop[0].toLowerCase())
                      return true;
                    if (u[0].toLowerCase() == 'therapists' && (prop[0].toLowerCase() == 'bullhorntalentid' || prop[0].toLowerCase() == 'talenttype')) {
                      return true;
                    }
                    return false;
                  }) : undefined;
                  if (p !== undefined) {
                    if (p[0] == 'therapists') {
                      let therapistVar: any;
                      p[1].map((therapistItem: any) => {
                        if (therapistItem.disciplineId == disciplineIdProp[1]) {
                          if (prop[0].toLowerCase() == 'bullhorntalentid')
                            therapistVar = therapistItem.bullhornTalentId;
                          if (prop[0].toLowerCase() == 'talenttype')
                            therapistVar = therapistItem.talentType;
                        }
                      });

                      p = [...prop];
                      p[1] = therapistVar;

                    }
                  }
                  const isPayrateChange = prop[0] === 'payrates' || prop[0] === 'payratesToDelete';

                  return (
                    <Fragment key={`pca-prop-key-${key + s.userId}`}>
                      {(p !== undefined || isPayrateChange) && Array.isArray(p) && p.length > 0 && prop !== undefined && prop[0] !== "HasPendingChanges" && prop[1] !== null && prop[1] !== undefined /*&& prop[1]?.length > 0*/ && (
                        <tr key={`pc-prop-key-${key + s.userId}`} className="pt-0 pb-0 mt-0 mb-0 text-left justify-left">
                          <td>
                            {prop[0] !== "payrates" && prop[0] !== "payratesToDelete" && (
                              <Fragment>
                                {pendingChangeValueLabel(p[0], p[1])}
                              </Fragment>
                            )}
                          </td>
                          {/* <td className="font-semibold">{displayChangesHeading(prop.old.key)}</td> */}
                          <td className="font-semibold">{pendingChangePropertyLabel(prop[0])}</td>
                          <td>
                            {pendingChangeValueLabel(prop[0], prop[1])}
                          </td>
                        </tr>
                      )}
                      {populatePendingPayrates(prop)}
                    </Fragment>
                  )
                })}
              </Fragment>
            </tbody>
          </table>
        );
      },
      width: 250,
      disableSortBy: true,
      sortable: false,
    },
    {
      Header: 'Actions',
      accessor: (s: any) => (
        <>
          <button
            className='mr-3 primary'
            onClick={(e) => {
              approve.run(s.userId);
              e.stopPropagation();
            }}
          >
            Approve
          </button>
          <button
            className="bg-red-600 hover:bg-red-700"
            onClick={(e) => {
              setTherapistId(s.userId);
              setOpenRejectConfirmModal(true);
              e.stopPropagation();
            }}
          >
            Reject
          </button>
        </>
      ),
      disableSortBy: true,
      sortable: false,

    },
    {
      accessor: 'userId',
      className: 'hidden',
    },
    {
      accessor: 'therapistId',
      className: 'hidden',
    },
    {
      accessor: 'userFirstName',
      className: 'hidden',
    },
    {
      accessor: 'userLastName',
      className: 'hidden',
    },
  ];

  /**
   * Therapist bonus approval logic
   * @param args 
   * @returns 
   */
  const sendApproveBonus = async (args: any) => await approvePendingBonus(args[0])
  const approveBonus = useAsync({
    deferFn: sendApproveBonus,
    onResolve: (response: any) => {
      setBonuses(response.data);
      setMessage('Bonus successfully approved');
    },
    onReject: (data) => { console.log(data) }
  });

  /**
   * Therapist change approval logic
   */
  const sendApprove = async (args: any) => await approvePendingTherapistChanges(args[0])
  const approve = useAsync({
    deferFn: sendApprove,
    onResolve: (response: any) => {
      //If the response was successful, remove the row
      setTherapistsData(response.data);
      //Set the notification message
      setMessage('Therapist changes successfully approved');
    },
    onReject: (data) => { console.log(data) }
  });

  /**
   * Therapist bonus Rejection Logic
   */
  const sendRejectBonus = async (args: any) => await rejectPendingBonus(args[0], { feedback: args[1] });

  /**
   * Therapist bonus rejection async
   */
  const rejectBonus = useAsync({
    deferFn: sendRejectBonus,
    onResolve: (response: any) => {
      setBonuses(response.data);
      setMessage('');
      setError('');
      setOpenRejectConfirmBonusModal(false);
      setFeedback('');
    },
    onReject: (error) => {
      setError('');
      setMessage('');
      setOpenRejectConfirmBonusModal(false);
    }
  });

  /**
   * Therapist change Rejection Logic
   */
  const sendReject = async (args: any) => await rejectPendingTherapistChanges(args[0], { feedback: args[1] });

  /**
   * Therapist change Rejection Async
   */
  const reject = useAsync({
    deferFn: sendReject,
    onResolve: (response: any) => {
      //If the response was successful, remove the row
      setTherapistsData(response.data);
      //Set the notification message
      setMessage('');
      setError('');
      setOpenRejectConfirmModal(false);
      setFeedback('');
    },
    onReject: (error) => {
      setError('');
      setMessage('');
      setOpenRejectConfirmModal(false);
    }
  });

  if (error) return <LayoutError />;

  /**
   * If the user is not a director, then we show the normal table
   */
  if (!hasRole(userRoles!, Role.Director))
    return (
      <Layout isLoading={
        isPending ||
        approve.isPending ||
        reject.isPending ||
        statesData.isPending ||
        districtsData.isPending
      }>
        <Fragment>
          {message !== '' && <Notification>{message}</Notification>}
          {error !== '' && <Notification>{error}</Notification>}
          {/** Rejection confirmation modal */}
          <EditModal
            open={false}
            saveText='Confirm'
            onSave={() => { reject.run(therapistId); }}
            onCancel={() => { setOpenRejectConfirmModal(false) }}
            id={therapistId}
          ><h3>Are you sure you would like to reject the requested changes for this therapist?</h3>
          </EditModal>

          {/** Rejection confirmation modal */}
          <EditModal
            open={openRejectConfirmModal}
            allowSave={false}
            saveColor='bg-green-500 hover:bg-green-500'
            removeText='Reject'
            removeColor='text-white bg-red-500 hover:bg-red-500'
            onRemove={() => {
              reject.run(therapistId, feedback);
            }}
            onCancel={() => { setFeedback(''); setOpenRejectConfirmModal(false) }}
          >
            <Fragment>
              <h3>Reject Changes</h3>
              <Fragment>
                <div className='mt-2 '>

                  <h3 className='mt-2 mb-2'>Are you sure you would like to reject the requested changes for this therapist?</h3>

                  <div className='w-full mt-2 mb-2'>
                    <div className='w-full flex-column'>
                      <div className='text-sm font-bold'>Feedback</div>
                      <div className='w-full'>
                        <textarea
                          className='w-full'
                          onChange={(e) => { setFeedback(e.currentTarget.value) }}
                          value={feedback ?? ''}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Fragment>

            </Fragment>
          </EditModal>

          {/** DataTable */}
          <div className='grid grid-cols-5 gap-5 mb-6'>
            <div className='col-span-5 sm:col-span-5'>
              <div className='overflow-hidden border-b border-gray-200 rounded-lg therapistsApprove datatable-editable'>
                {therapistsData.length > 0 && (
                  <DataTable
                    key={'pc-datatable'}
                    columns={columns()}
                    data={therapistsData}
                    showPagination={true}
                    getRowProps={(row: any) => ({
                      className: "cursor-pointer hover:bg-gray-100",
                      onClick: () => history.push(`/admin/therapists/${row.values.userId}`)
                    })}
                  />
                )}
                {(therapistsData.length === 0 || therapistsData === undefined) && <h3>No pending therapist changes were found</h3>}
              </div>
            </div>
          </div>
        </Fragment>
      </Layout>
    );

  /**
   * If user is a director, we show bonuses
   */
  return (
    <Layout isLoading={
      isPending ||
      approve.isPending ||
      reject.isPending ||
      statesData.isPending ||
      districtsData.isPending
    }>
      <Fragment>
        {message !== '' && <Notification>{message}</Notification>}
        {error !== '' && <Notification>{error}</Notification>}
        <EditModal
          open={false}
          saveText='Confirm'
          onSave={() => { rejectBonus.run(bonusId); }}
          onCancel={() => { setOpenRejectConfirmBonusModal(false) }}
          id={bonusId}
        ><h3>Are you sure you would like to reject the requested bonus for this therapist?</h3>
        </EditModal>
        <EditModal
          open={openRejectConfirmBonusModal}
          allowSave={false}
          saveColor='bg-green-500 hover:bg-green-500'
          removeText='Reject'
          removeColor='text-white bg-red-500 hover:bg-red-500'
          onRemove={() => {
            rejectBonus.run(bonusId, feedback);
          }}
          onCancel={() => { setFeedback(''); setOpenRejectConfirmBonusModal(false); }}
        >
          <Fragment>
            <h3>Reject Bonus</h3>
            <Fragment>
              <div className='mt-2 '>

                <h3 className='mt-2 mb-2'>Are you sure you would like to reject the requested bonus for this therapist?</h3>

                <div className='w-full mt-2 mb-2'>
                  <div className='w-full flex-column'>
                    <div className='text-sm font-bold'>Feedback</div>
                    <div className='w-full'>
                      <textarea
                        className='w-full'
                        onChange={(e) => { setFeedback(e.currentTarget.value) }}
                        value={feedback ?? ''}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>

          </Fragment>
        </EditModal>
        <div className='grid grid-cols-6 gap-2 mb-6'>
          {bonuses.length > 0 && (
            <div className='col-span-6'>
              <div className="overflow-hidden border-b border-gray-200 rounded-lg shadow">
                <table className='w-full min-w-full text-sm text-left'>
                  <thead className='font-bold text-center bg-ptsblue-normal'>
                    <tr>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">Therapist</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">Type</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">Frequency</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">Amount</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">Payment Amount</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">Start</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">End</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group">Comment</th>
                      <th className="h-12 px-3 py-3 text-xs tracking-wider text-left text-white uppercase group"></th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {bonuses.map((b: Bonus) => (
                      <tr
                        key={`bonus_${b.bonusId}`}
                        className='h-16 cursor-pointer hover:bg-gray-100'
                        id={`bonus_${b.bonusId}`}
                      >
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'>{b.therapist?.lastName}, {b.therapist?.firstName}</div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'>{b.type}</div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'>{b.frequency}</div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'>{b.amount}</div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'>{b.paymentAmounts}</div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'><>{b.startDate !== null ? moment(b.startDate).format('l') : 'N/A'}</></div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'><>{b.endDate !== null ? moment(b.endDate).format('l') : 'N/A'}</></div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'>{b.comments}</div></td>
                        <td className='px-3 py-3 preak-word'><div className='text-sm text-gray-900'>
                          <button
                            className='mr-3 primary'
                            onClick={(e) => {
                              approveBonus.run(b.bonusId);
                              e.stopPropagation();
                            }}
                          >
                            Approve
                          </button>
                          <button
                            className="bg-red-600 hover:bg-red-700"
                            onClick={(e) => {
                              setBonusId(b.bonusId!);
                              setOpenRejectConfirmBonusModal(true);
                              e.stopPropagation();
                            }}
                          >
                            Reject
                          </button></div>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/** Bonuses Not Found */}
          {bonuses.length === 0 && (
            <div className='col-span-5 mb-6 sm:col-span-5'>
              <label className='primary text-md'>No Pending Bonuses Found</label>
            </div>
          )}
        </div>
      </Fragment>
    </Layout>
  );
};

export default ApproveTherapist;